<template>
    <form v-if="status.detail" @submit.prevent="userUUID ? requestUpdate() : requestCreate()">
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-account mr-1"></i>
                    Basic Information
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group :label="$t('label.image')" label-cols="2" label-align="left">
                    <ImageUploader :multiple="false" :showImageTop="true"
                        :buttonText="$t('label.uploadX', [$t('label.image')])" @images-changed="uploadImage"
                        @images-removed="removeImage" :initial-images="createForm.image ? [createForm.image] : []"
                        ref="coverUploader" />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.firstName')" label-cols="4" label-align="left">
                    <b-form-input v-model.trim="createForm.firstName"
                        :class="{ 'is-invalid': submitted && !$v.createForm.firstName.required }"
                        autocomplete="off"></b-form-input>
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.firstName.required" class="text-right">
                        {{ $t("message.requiredX", [$t("label.firstName")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.lastName')" label-cols="4" label-align="left">
                    <b-form-input v-model.trim="createForm.lastName"
                        :class="{ 'is-invalid': submitted && !$v.createForm.lastName.required }"
                        autocomplete="off"></b-form-input>
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.lastName.required" class="text-right">
                        {{ $t("message.requiredX", [$t("label.lastName")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.email')" label-cols="4" label-align="left">
                    <b-form-input v-model.trim="createForm.email"
                        :class="{ 'is-invalid': submitted && $v.createForm.email.$error }"
                        autocomplete="off"></b-form-input>
                    <div v-if="submitted && $v.createForm.email.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.email.required">{{ $t("message.requiredX", [$t("label.email")])
                            }}</span>
                        <span v-if="!$v.createForm.email.email">{{ $t("message.invalidX", [$t("label.email")]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.phone')"
                    label-cols="4"
                    label-align="left"
                >
                    <vue-tel-input
                        v-on:country-changed="(country) => phoneCountryChanged(country)"
                        v-model.trim="createForm.phone"
                        :defaultCountry="createForm.phoneIso || 'MY'"
                        :autoDefaultCountry="false"
                        :autoFormat="false"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.portalPassword')" label-cols="4" label-align="left">
                    <b-input-group>
                        <b-form-input
                            v-model="createForm.portalPassword"
                            :type="showPortalPassword ? 'text' : 'password'"
                            :disabled="!status.save"
                            :class="{ 'is-invalid': submitted && !$v.createForm.portalPassword.required }"
                            autocomplete="new-password"
                        />
                        <b-input-group-append>
                            <b-input-group-text class="cursor-pointer" @click="showPortalPassword = !showPortalPassword">
                                <i :class="showPortalPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'"></i>
                            </b-input-group-text>
                        </b-input-group-append>
                    </b-input-group>
                    <div v-if="submitted && !$v.createForm.portalPassword.required" class="invalid-feedback d-block text-right">
                        {{ $t('message.requiredX', [$t('label.portalPassword')]) }}
                    </div>
                </b-form-group>
            </div>
        </div>
        <b-alert show variant="info" class="mb-4">
            <div class="font-weight-bold mb-1"><i class="mdi mdi-account-arrow-right-outline mr-1"></i>External user</div>
            This account is intended for external users (e.g., Mandrill), with access restricted to the Meeting Room Booking portal only.
        </b-alert>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button type="button" variant="danger" class="mr-2 col-3 col-md-2" :disabled="!status.save" @click="$emit('done')">
                {{ $t('label.cancel') }}
            </b-button>
            <b-button type="submit" variant="info" class="col-3 col-md-2" :disabled="!status.save">
                <span v-if="status.save">{{ userUUID ? $t('label.updateX', []) : $t('label.createX', []) }}</span>
                <b-spinner v-else small />
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-3">
        <b-spinner class="m-5" variant="info" role="status" />
    </div>
</template>
<script>
import { VueTelInput } from 'vue-tel-input';
import ImageUploader from '@/components/upload-image';
import { required, requiredIf, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { userMethods } from "@/state/api";
import { USER_MODULE } from "@/state/api/user.state";

export default {
    props: {
        userUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    components: { ImageUploader, VueTelInput },
    validations() {
        return {
            createForm: {
                firstName: { required },
                lastName: { required },
                email: { required, email },
                portalPassword: { required: requiredIf(() => !this.userUUID) },
            },
        };
    },
    async created() {
        await this.getUserData();
    },
    data() {
        return {
            submitted: false,
            showPortalPassword: false,
            createForm: this.defaultForm(),
        };
    },
    methods: {
        ...userMethods,
        defaultForm() {
            return {
                uuid: "",
                image: null,
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                phoneCode: "+60",
                phoneIso: "MY",
                portalPassword: "",
            };
        },
        async getUserData() {
            if (this.userUUID) {
                await this.getUser({uuid: this.userUUID})
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                image: this.data.image,
                                firstName: this.data.firstName,
                                lastName: this.data.lastName,
                                email: this.data.email,
                                phone: String(this.data.phone || ''),
                                phoneCode: this.data.phoneCode,
                                phoneIso: this.data.phoneIso,
                                portalPassword: "",
                            };
                        }
                    });
            }
        },
        uploadImage(image) {
            this.createForm.image = image[0]?.file;
        },
        removeImage() {
            this.createForm.image = null;
        },
        phoneCountryChanged(country) {
            this.createForm.phoneCode = "+" + country.dialCode;
            this.createForm.phoneIso = country.iso2;
        },
        requestCreate() {
            if (!this.status.save) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    userType: 1,
                };
                return this.createUser({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit('done');
                        }
                    }));
            }
        },
        requestUpdate() {
            if (!this.status.save) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    image: typeof this.createForm.image == 'string' ? undefined : this.createForm.image,
                    userType: 1,
                };
                return this.updateUser({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit('done');
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.showPortalPassword = false;
            this.submitted = false;
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(USER_MODULE, ['status']),
        data() {
            return this.$store.state.api.user.details;
        },
    },
};
</script>
